<script setup lang="ts">
  import _ from "lodash";
  import { storeToRefs } from "pinia";
  import type { DraggableItem } from "~/entities/DraggableItem";
  import { PlanInfo } from "~/entities/PlanInfo";
  import type { TalkRoom } from "~/entities/TalkRoom";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
    selectedTalkRoomId: string | undefined;
    talkRooms: TalkRoom[];
    isOpen: boolean;
    searchWord: string;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:isOpen", isOpen: boolean): void;
    (event: "update:searchWord", searchWord: string): void;
  }
  const emit = defineEmits<Emits>();
  const computedIsOpen = computed<boolean>({
    get: () => props.isOpen,
    set: value => {
      emit("update:isOpen", value);
    },
  });
  const computedSearchWord = computed<string>({
    get: () => props.searchWord,
    set: value => {
      emit("update:searchWord", value);
    },
  });

  const planId = props.planInfo.id;
  const isOwner = computed<boolean>(() => props.planInfo.isOwner);

  const headerAreaRef = ref<HTMLElement | null>(null);
  const { height: headerAreaElementHeight } = useElementBounding(headerAreaRef);
  const listAreaElementHeight = computed(() => {
    return (
      document.body.clientHeight - // 画面全体の高さを使うのが最も安定したので採用
      headerAreaElementHeight.value -
      70
    ); // 実績ベースで margin・padding を差し引いた
  });
  const searchTextFieldRef = ref<HTMLInputElement | null>(null);

  // トーク検索結果取得
  const talkRoomStore = useTalkRoomStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const SEARCH_COUNT_MAX = 100;

  const groupTalksKey = ref(1);
  const groupTalkCountsRef = ref<DraggableItem[]>([]);
  const convertGroupTalkRoom = (talkRoom: TalkRoom, count: number) => {
    return {
      id: talkRoom.talkRoomId,
      label: `${talkRoom.roomName} (${count})`,
      canView: true,
      hasUnread: false,
      imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
      isUserIcon: false,
      isOwner: false,
      onClick: onTalkRoomClick,
    } as DraggableItem;
  };

  const dMTalksKey = ref(1);
  const dMTalkCountsRef = ref<DraggableItem[]>([]);
  const convertDMTalkRoom = (talkRoom: TalkRoom, count: number) => {
    return {
      id: talkRoom.talkRoomId,
      label: `${dMTalkRoomLabel(talkRoom, props.planInfo)} (${count})`,
      canView: true,
      hasUnread: false,
      imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
      isUserIcon: true,
      isOwner: !isOwner.value, // 講師はユーザー情報を、ユーザーは講師情報を見る
      onClick: onTalkRoomClick,
    } as DraggableItem;
  };

  const isGroupTalkOpen = ref(true);
  const isDMTalkOpen = ref(true);

  enum LoadingState {
    Init,
    Loading,
    Loaded,
  }
  const loadingState = ref(LoadingState.Init);
  const initResult = () => {
    loadingState.value = LoadingState.Init;
    groupTalkCountsRef.value = [];
    dMTalkCountsRef.value = [];
  };
  const isLoading = computed(() => loadingState.value === LoadingState.Loading);
  const isLoaded = computed(() => loadingState.value === LoadingState.Loaded);
  const load = async () => {
    if (loadingState.value === LoadingState.Loading) {
      // load 連発防止
      return;
    }

    // 検索結果を初期化
    groupTalkCountsRef.value = [];
    dMTalkCountsRef.value = [];

    // 検索開始
    loadingState.value = LoadingState.Loading;
    try {
      const talkCounts = await talkRoomStore.searchTalkCountsInPlan(planId, computedSearchWord.value, SEARCH_COUNT_MAX);
      const totalCount = talkCounts.reduce((acc, talkCount) => acc + talkCount.count, 0);
      if (totalCount >= SEARCH_COUNT_MAX) {
        snackBarStore.setError(
          `検索結果が多すぎます（${SEARCH_COUNT_MAX}件まで表示しています）。キーワードを絞ってください。`,
        );
      }

      // チャンネルと DM に分ける（アーカイブは API 側で除外済み）
      const findRoom = (talkRoomId: string) => props.talkRooms.find(talkRoom => talkRoom.talkRoomId === talkRoomId)!;

      const groupTalkCounts = talkCounts.filter(talkCount => {
        if (talkCount.count === 0) return false;
        const room = findRoom(talkCount.talkRoomId);
        return room && !room.isArchive && !room.isDirectMessage;
      });
      groupTalkCountsRef.value = groupTalkCounts.map(talkCount =>
        convertGroupTalkRoom(findRoom(talkCount.talkRoomId), talkCount.count),
      );
      groupTalksKey.value++;

      const dMTalkCounts = talkCounts.filter(talkCount => {
        if (talkCount.count === 0) return false;
        const room = findRoom(talkCount.talkRoomId);
        return room && !room.isArchive && room.isDirectMessage;
      });
      dMTalkCountsRef.value = dMTalkCounts.map(talkCount =>
        convertDMTalkRoom(findRoom(talkCount.talkRoomId), talkCount.count),
      );
      dMTalksKey.value++;
      loadingState.value = LoadingState.Loaded;
    } catch (error: any) {
      // 検索失敗時は初期化してエラー表示
      initResult();
      snackBarStore.setError(`トークの検索に失敗しました`);
    } finally {
      // disabled だとフォーカスあたらないので少し待ってからフォーカス
      setTimeout(() => searchTextFieldRef.value?.focus(), 500);
    }
  };

  // 検索
  const debouncedSearch = _.debounce(load, props.isTest ? 1 : 1000); // 1秒デバウンスして検索。テスト時は即時検索
  watch(computedSearchWord, () => {
    if (canSearchTalk(computedSearchWord.value)) {
      // 検索可能なワードの場合はデバウンスして検索
      debouncedSearch();
    } else {
      // 検索不可な場合は初期化
      initResult();
    }
  });

  // トークルームを開く
  const snackBarStore = useSnackBarStore();
  const { canSearch, searchOpen, searchText } = storeToRefs(talkRoomTmpViewStore);
  const onTalkRoomClick = (talkRoomId: string) => {
    // まず検索可能フラグを落とす
    canSearch.value = false;

    if (props.selectedTalkRoomId === talkRoomId) {
      // 既に開いているトークルームを選択した場合は検索フラグを立てる
      // 直接立てると検索処理側初期化処理と競合してしまうため、 nextTick で遅延させる
      // (結果、ルーム切り替えと類似のタイミングで処理される)
      nextTick(() => {
        canSearch.value = true;
      });
    } else {
      // 指定のトークルームを開く
      // 表示完了後に検索可能フラグが立つ
      historyStore.to(`/plan/${planId}?tab=talk&room=${talkRoomId}`);
    }

    // 検索ワード設定＋検索ダイアログを開く
    searchText.value = computedSearchWord.value;
    searchOpen.value = true;

    // 本ダイアログは閉じる（検索ワードは残す）
    computedIsOpen.value = false;
  };

  onMounted(() => {
    load();
  });

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <v-dialog
    v-model="computedIsOpen"
    class="mx-0"
    :width="isSp ? '100%' : '70%'"
    height="100%"
    scrim="black"
    :attach="!!isTest"
  >
    <v-card class="d-flex flex-column justify-center dialog-body" :class="[isSp ? 'pt-8 pb-4' : 'py-4']">
      <div class="close-button" @click="computedIsOpen = false">✕</div>

      <div ref="headerAreaRef" class="dialog-header">
        <div class="search-title text-center">トークを探す</div>
        <div class="search-wrapper">
          <v-text-field
            ref="searchTextFieldRef"
            v-model="computedSearchWord"
            class="search-input"
            placeholder="メッセージ検索"
            prepend-inner-icon="$mdiMagnify"
            variant="solo"
            autofocus
            clearable
            :loading="isLoading"
            :disabled="isLoading"
            @keydown.enter.prevent
          ></v-text-field>
        </div>
      </div>

      <div v-if="isLoaded && groupTalkCountsRef.length === 0 && dMTalkCountsRef.length === 0">
        <div class="text-center text-value">検索結果がありません</div>
      </div>

      <v-card-text class="dialog-list" :style="`height:${listAreaElementHeight}px`">
        <MoleculesExpansionPanel
          v-if="groupTalkCountsRef.length > 0"
          v-model:is-open="isGroupTalkOpen"
          data-testid="talk-message-search-group-panel"
        >
          <template #title>
            <div class="d-flex w-100">
              <div class="my-auto mr-auto d-flex" :class="isSp ? `panel-title-sp` : `panel-title`">
                <div :class="isSp ? `panel-title-label-sp` : `panel-title-label`">チャンネル</div>
              </div>
            </div>
          </template>
          <MoleculesDraggableList :key="groupTalksKey" v-model:items="groupTalkCountsRef"></MoleculesDraggableList>
        </MoleculesExpansionPanel>

        <MoleculesExpansionPanel
          v-if="dMTalkCountsRef.length > 0"
          v-model:is-open="isDMTalkOpen"
          data-testid="talk-message-search-dm-panel"
        >
          <template #title>
            <div class="d-flex w-100">
              <div class="my-auto mr-auto d-flex" :class="isSp ? `panel-title-sp` : `panel-title`">
                <div :class="isSp ? `panel-title-label-sp` : `panel-title-label`">DM</div>
              </div>
            </div>
          </template>
          <MoleculesDraggableList :key="dMTalksKey" v-model:items="dMTalkCountsRef"></MoleculesDraggableList>
        </MoleculesExpansionPanel>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .dialog-body {
    height: 100%;
    color: white;
    background-color: #222222;
  }
  .close-button {
    position: absolute;
    top: 0;
    right: 0;
    color: white;
    margin-top: 20px;
    margin-right: 20px;
    cursor: pointer;
    z-index: 1;
  }
  .dialog-header {
    position: absolute;
    top: 20px;
    width: 100%;
    padding: 0 20px;
  }
  .dialog-list {
    position: absolute;
    top: 140px;
    width: 100%;
    padding: 0 8px !important;

    overflow-y: auto;
    // set scrollbar none
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .search-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.8em;
    margin-bottom: 20px;
  }
</style>

<style lang="scss">
  .v-dialog > .v-overlay__content {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }
</style>
